import React from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import { PERMISSION_KEYS } from 'constants-permissions';
import { hasPermission } from 'lane-shared/helpers';
import { Button } from 'design-system-web';
import { Flex } from 'components';
import { H3, H4 } from 'components/typography';

import type { Channel } from 'lane-shared/types/ChannelType';
import type { UserType } from 'lane-shared/types/User';

import { useReports } from './hooks/useReports';
import styles from './styles.scss';

interface Props {
  channel: Pick<Channel, '_id' | 'name'>;
  user: UserType;
}

const NotAuthorizedView = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.notAuthorizedWrapper}>
      <div className={styles.notAuthorized}>
        <h1>{t('web.admin.workOrder.reporting.notAuthorized')}</h1>
      </div>
    </div>
  );
};

export function Reporting({ channel, user }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const { reportCategories } = useReports();

  const canAccessReporting =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_ANALYTICS_WORK_ORDERS],
      channel?._id,
      false
    );

  if (!canAccessReporting) {
    return (
      <div className={styles.reporting}>
        <NotAuthorizedView />
      </div>
    );
  }

  return (
    <div className={styles.reporting}>
      <Flex className={styles.header} m={1} mb={2}>
        <div className={styles.titleWrapper}>
          <H3>{t('web.admin.workOrder.reporting.title')}</H3>
        </div>
      </Flex>

      <div className={styles.reportCategoriesContainer}>
        {reportCategories.map(category => (
          <div key={category.id} className={styles.reportsSection}>
            <H4 className={styles.sectionTitle}>{category.title}</H4>

            <div className={styles.reportsList}>
              {category.reports.map(report => (
                <div key={report.id} className={styles.reportItem}>
                  <div className={styles.reportName}>{report.displayName}</div>
                  <Link to={`${history.location.pathname}/${report.id}`}>
                    <Button
                      variant="text"
                      size="medium"
                      testId={`run-report-button-${report.id}`}
                    >
                      {t('web.admin.workOrder.reporting.runReport')}
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
